import { IButtonStyles } from '@fluentui/react/lib/Button';
import { IRawStyle, ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors } from '../teams-colors';
import { ShadowLevels } from '../teams-shadow-levels';
import { teamsButtonBorderRadius } from './constants';

export function getPrimaryButtonStyles(
  themeName: string,
  theme: ITheme
): Partial<IButtonStyles> {
  let iconFillColor = theme.palette.white;

  if (themeName === 'dark') {
    iconFillColor = theme.palette.black;
  }
  if (themeName === 'contrast') {
    iconFillColor = theme.palette.white;
  }

  const styles = {
    root: {
      borderRadius: teamsButtonBorderRadius,
      backgroundColor: theme.palette.themePrimary,
      color: theme.palette.white,
      boxShadow: ShadowLevels.shadowLevel1Dark,
      borderColor: theme.palette.themePrimary,
      selectors: {
        'svg, .icons-default-fill': {
          fill: iconFillColor
        }
      }
    },
    rootHovered: {
      backgroundColor: theme.palette.themeDark,
      borderColor: theme.palette.themeDark,
      color: theme.palette.white,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.white
        }
      }
    },
    rootPressed: {
      backgroundColor: theme.palette.themeDarker,
      borderColor: theme.palette.themeDarker,
      color: theme.palette.white,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.white
        }
      }
    },
    rootChecked: {
      backgroundColor: theme.palette.themeDarker,
      borderColor: theme.palette.themeDarker,
      color: theme.palette.white,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.white
        }
      }
    },
    rootCheckedHovered: {
      backgroundColor: theme.palette.themeDark,
      borderColor: theme.palette.themeDark,
      color: theme.palette.white,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.white
        }
      }
    },
    rootDisabled: {
      backgroundColor: theme.palette.neutralQuaternary,
      borderColor: theme.palette.neutralQuaternary,
      color: theme.palette.neutralTertiary,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.neutralTertiary
        }
      }
    },
    rootFocused: {
      backgroundColor: theme.palette.themePrimary,
      borderColor: theme.palette.themePrimary,
      borderRadius: teamsButtonBorderRadius,
      color: theme.palette.white,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.white
        }
      }
    }
  } as Partial<IButtonStyles>;

  // Dark theme overrides
  if (themeName === 'dark') {
    // Root
    (styles.root as IRawStyle).color = theme.palette.black;
    (styles.root as IRawStyle).backgroundColor = theme.palette.themeTertiary;
    (styles.root as IRawStyle).borderColor = theme.palette.themeTertiary;

    // Root Hovered
    (styles.rootHovered as IRawStyle).color = theme.palette.black;
    (styles.rootHovered as IRawStyle).backgroundColor =
      theme.palette.themeSecondary;
    (styles.rootHovered as IRawStyle).borderColor =
      theme.palette.themeSecondary;
    ((styles.rootHovered as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.black;

    // Root Checked
    (styles.rootChecked as IRawStyle).color = theme.palette.black;
    (styles.rootChecked as IRawStyle).backgroundColor =
      theme.palette.themeLight;
    (styles.rootChecked as IRawStyle).borderColor = theme.palette.themeLight;
    ((styles.rootChecked as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.black;

    // Root Checked Hovered
    (styles.rootCheckedHovered as IRawStyle).color = theme.palette.black;
    (styles.rootCheckedHovered as IRawStyle).backgroundColor =
      theme.palette.themeSecondary;
    (styles.rootCheckedHovered as IRawStyle).borderColor =
      theme.palette.themeSecondary;
    ((styles.rootCheckedHovered as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.black;

    // Root Pressed
    (styles.rootPressed as IRawStyle).color = theme.palette.black;
    (styles.rootPressed as IRawStyle).backgroundColor =
      theme.palette.themeLight;
    (styles.rootPressed as IRawStyle).borderColor = theme.palette.themeLight;
    ((styles.rootPressed as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.black;

    // Root Focused
    (styles.rootFocused as IRawStyle).backgroundColor =
      theme.palette.themeTertiary;
    (styles.rootFocused as IRawStyle).borderColor = theme.palette.themeTertiary;
    (styles.rootFocused as IRawStyle).color = theme.palette.black;
    ((styles.rootFocused as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.black;

    // Root Disabled
    ((styles.rootDisabled as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.neutralTertiary;
  }

  // Contrast theme overrides
  if (themeName === 'contrast') {
    // Root
    (styles.root as IRawStyle).color = theme.palette.white;
    (styles.root as IRawStyle).backgroundColor = theme.palette.black;
    (styles.root as IRawStyle).borderColor = theme.palette.black;

    // Root Hovered
    (styles.rootHovered as IRawStyle).backgroundColor = theme.palette.themeDark;
    (styles.rootHovered as IRawStyle).borderColor = theme.palette.themeDark;

    // Root Pressed
    (styles.rootPressed as IRawStyle).backgroundColor = theme.palette.themeDark;
    (styles.rootPressed as IRawStyle).borderColor = theme.palette.themeDark;

    // Root Checked
    (styles.rootChecked as IRawStyle).backgroundColor =
      theme.palette.themePrimary;
    (styles.rootChecked as IRawStyle).borderColor = theme.palette.themePrimary;

    // Root Checked Hovered
    (styles.rootChecked as IRawStyle).backgroundColor = theme.palette.themeDark;
    (styles.rootChecked as IRawStyle).borderColor = theme.palette.themeDark;

    // Root Disabled
    (styles.rootDisabled as IRawStyle).color = theme.palette.white;
    (styles.rootDisabled as IRawStyle).backgroundColor =
      ContrastColors.appGreenContrast;
    (styles.rootDisabled as IRawStyle).borderColor =
      ContrastColors.appGreenContrast;
    ((styles.rootDisabled as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.white;

    // Root Focused
    (styles.rootFocused as IRawStyle).backgroundColor = theme.palette.themeDark;
    (styles.rootFocused as IRawStyle).borderColor = theme.palette.themeDark;
  }
  return styles;
}
